import { useState, useEffect } from 'react';
import StatCard from "../../../components/common/StatCard";
import ComponentCard from "../../../components/common/ComponentCard";
import Button from "../../../components/ui/button/Button";
import { DollarSign, Clock, Plus, Download, TrendingUp, Users, BarChart, RefreshCw, Check, AlertCircle, Info } from 'lucide-react';
import useApi, { Ingreso, IngresoStats, ExportFilters, SyncResult } from "../../../hooks/useApi";
import IngresosList from "./IngresosList";
import { Modal } from "../../../components/ui/modal";
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

export default function IngresosView() {
  const { 
    getIngresos, 
    getIngresoStats, 
    exportIngresos,
    syncIngresos,
    formatCurrency,
    getClientes,
    getAgencias
  } = useApi();
  
  const [ingresos, setIngresos] = useState<Ingreso[]>([]);
  const [stats, setStats] = useState<IngresoStats>({
    totalIngresos: 0,
    ingresosDelMes: 0,
    clientesConPagos: 0,
    porEstado: {
      completados: 0,
      parciales: 0
    },
    tendencias: {
      ingresos: 0,
      clientes: 0
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState<ExportFilters>({
    startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    cliente: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{id: number, name: string, type: 'cliente' | 'agencia'}>>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{id: number, name: string, type: 'cliente' | 'agencia'} | null>(null);

  const refreshStats = async () => {
    try {
      const statsData = await getIngresoStats();
      if (statsData) {
        setStats(statsData as IngresoStats);
      }
    } catch (error) {
      toast.error(
        'No se pudieron cargar las estadísticas. Por favor, actualice la página.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    }
  };

  const refreshData = async () => {
    try {
      setIsLoading(true);
      
      await Promise.all([
        refreshStats(),
        getIngresos().then(ingresosData => {
          setIngresos(ingresosData);
        })
      ]);
      
    } catch (error) {
      toast.error(
        'Error al cargar los datos. Por favor, verifique su conexión e inténtelo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleOpenExportModal = () => {
    setExportFilters({
      startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      cliente: ''
    });
    setSearchTerm('');
    setSearchResults([]);
    setSelectedEntity(null);
    setIsExportModalOpen(true);
  };
  
  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    
    // Update the cliente field in exportFilters for free text search
    setExportFilters({
      ...exportFilters,
      cliente: value,
      clientId: undefined // Clear the ID since this is now a text search
    });
    
    if (value.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }
    
    setLoadingSearch(true);
    setShowSuggestions(true);
    
    try {
      // Fetch both clients and agencies and combine the results
      const [clients, agencies] = await Promise.all([
        getClientes(),
        getAgencias()
      ]);
      
      const filteredClients = clients
        .filter(client => client.name.toLowerCase().includes(value.toLowerCase()))
        .map(client => ({
          id: client.id,
          name: client.name,
          type: 'cliente' as const
        }));
        
      const filteredAgencies = agencies
        .filter(agency => agency.name.toLowerCase().includes(value.toLowerCase()))
        .map(agency => ({
          id: agency.id,
          name: agency.name,
          type: 'agencia' as const
        }));
      
      // Combine and sort results
      const combinedResults = [...filteredClients, ...filteredAgencies]
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setSearchResults(combinedResults);
    } catch (error) {
      console.error('Error searching clients and agencies', error);
      toast.error(
        'Error al buscar clientes y agencias',
        {
          duration: 3000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setLoadingSearch(false);
    }
  };
  
  const handleSelectEntity = (entity: {id: number, name: string, type: 'cliente' | 'agencia'}) => {
    setSelectedEntity(entity);
    setSearchTerm(entity.name);
    setExportFilters({
      ...exportFilters,
      cliente: entity.name,
      clientId: entity.id
    });
    setShowSuggestions(false);
  };
  
  const handleExportData = async () => {
    try {
      const blob = await exportIngresos(exportFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ingresos_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      setIsExportModalOpen(false);
      toast.success(
        'Datos exportados correctamente a Excel',
        {
          duration: 4000,
          icon: <Check className="w-4 h-4 text-green-500" />,
        }
      );
    } catch (error) {
      toast.error(
        'No se pudieron exportar los datos. Verifique los filtros e inténtelo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
      console.error(error);
    }
  };

  const handleSyncIngresos = async () => {
    try {
      setIsSyncing(true);
      const result = await syncIngresos();
      
      if (result.ingresosCreated === 0 && result.ingresosDeleted === 0) {
        toast(
          'No hay nuevos cambios para sincronizar',
          {
            duration: 3000,
            icon: <Info className="w-4 h-4 text-blue-500" />,
          }
        );
      } else {
        toast.success(
          `Sincronización completada`,
          {
            duration: 5000,
            icon: <Check className="w-4 h-4 text-green-500" />,
          }
        );
      }
      
      // Refresh data after sync
      await refreshData();
    } catch (error) {
      toast.error(
        'Error al sincronizar con facturas. Por favor, inténtelo más tarde.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
      console.error(error);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Ingresos totales"
              value={isLoading ? '---' : formatCurrency(stats.totalIngresos)}
              trend={!isLoading && stats.tendencias ? stats.tendencias.ingresos : 0}
              icon={<DollarSign className="w-6 h-6" />}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Ingresos del mes"
              value={isLoading ? '---' : formatCurrency(stats.ingresosDelMes)}
              trend={0}
              icon={<TrendingUp className="w-6 h-6" />}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Clientes con pagos"
              value={isLoading ? '---' : stats.clientesConPagos.toString()}
              trend={!isLoading && stats.tendencias ? stats.tendencias.clientes : 0}
              icon={<Users className="w-6 h-6" />}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Comparación mensual"
              value={isLoading ? '---' : stats.tendencias && stats.tendencias.ingresos > 0 ? `+${stats.tendencias.ingresos.toFixed(2)}%` : `${stats.tendencias.ingresos.toFixed(2)}%`}
              trend={!isLoading && stats.tendencias ? stats.tendencias.ingresos : 0}
              icon={<BarChart className="w-6 h-6" />}
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          <IngresosList 
            ingresos={ingresos}
            onDataChange={refreshData}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ComponentCard title="Acciones rápidas">
            <div className="space-y-3 p-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={handleSyncIngresos}
                disabled={isSyncing}
              >
                {isSyncing ? 'Sincronizando...' : 'Sincronizar con facturas'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                startIcon={<Download className="w-4 h-4" />}
                onClick={handleOpenExportModal}
              >
                Exportar datos
              </Button>
            </div>
          </ComponentCard>

          <ComponentCard title="Resumen de estado">
            <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porEstado.completados}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">completados</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porEstado.parciales}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">parciales</span>
                  </div>
                </div>
              </div>
            </div>
          </ComponentCard>
        </div>
      </div>

      {/* Modal de exportación */}
      {isExportModalOpen && (
        <Modal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          title="Exportar ingresos a Excel"
        >
          <div className="space-y-4 pb-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Seleccione el período y el cliente/agencia para filtrar los ingresos a exportar
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha inicio
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={exportFilters.startDate}
                  onChange={(e) => setExportFilters({...exportFilters, startDate: e.target.value})}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={exportFilters.endDate}
                  onChange={(e) => setExportFilters({...exportFilters, endDate: e.target.value})}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="clienteSearch" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cliente o Agencia
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="clienteSearch"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="Buscar cliente o agencia (o ingresar nombre manual)"
                />
                
                {loadingSearch && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400"></div>
                  </div>
                )}
                
                {showSuggestions && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {searchResults.map((result) => (
                        <li 
                          key={`${result.type}_${result.id}`}
                          className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-800 dark:text-gray-200"
                          onClick={() => handleSelectEntity(result)}
                        >
                          <span className={`w-2 h-2 rounded-full mr-2 ${
                            result.type === 'cliente' ? 'bg-blue-500' : 'bg-green-500'
                          }`}></span>
                          <span>{result.name}</span>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({result.type === 'cliente' ? 'Cliente' : 'Agencia'})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {showSuggestions && searchTerm.length >= 2 && searchResults.length === 0 && !loadingSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-2 px-4 text-sm text-gray-500 dark:text-gray-400">
                    No se encontraron coincidencias. Se utilizará el texto ingresado.
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Busque y seleccione un cliente/agencia existente o ingrese un nombre personalizado.
              </p>
            </div>
            
            <div className="pt-4 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsExportModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleExportData}
              >
                Exportar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
